import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import { createProductCategory, resetCreateStatus, resetUpdateStatus, selectCreateCategoryError, selectCreateCategoryLoading, selectProductCategoriesList, selectSelectedCategory, selectSelectedCategoryLoading, selectUpdateCategoryError, selectUpdateCategoryLoading, updateProductCategory } from "../../redux/productCategory/productCategorySlice";
import { selectRightModalData, setHasToRefresh, setGoingToClose, closeModal } from "../../redux/rightModalSlice";
import { selectAccountData } from "../../redux/main/accountSlice";
import LoadingSection from "../LoadingSection";

export default function AddEditCategory({ mode }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const selectedCategory = useSelector(selectSelectedCategory);
    const selectedCategoryLoading = useSelector(selectSelectedCategoryLoading);
    const createLoading = useSelector(selectCreateCategoryLoading);
    const createError = useSelector(selectCreateCategoryError);
    const updateLoading = useSelector(selectUpdateCategoryLoading);
    const updateError = useSelector(selectUpdateCategoryError);
    const categoriesList = useSelector(selectProductCategoriesList);
    const modalData = useSelector(selectRightModalData);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        parentId: modalData?.parentId || '',
        active: true
    });

    const [validation, setValidation] = useState({
        name: true
    });

    useEffect(() => {
        if (mode === 'edit' && selectedCategory?._id) {
            setFormData({
                name: selectedCategory.name || '',
                description: selectedCategory.description || '',
                parentId: selectedCategory.parentId || '',
                active: selectedCategory.active !== undefined ? selectedCategory.active : true
            });
        }
    }, [mode, selectedCategory]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name in validation) {
            setValidation(prev => ({ ...prev, [name]: true }));
        }
    };

    const validateForm = () => {
        const newValidation = { name: !!formData.name.trim() };
        setValidation(newValidation);
        return Object.values(newValidation).every(v => v);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        if (mode === 'add') {
            dispatch(createProductCategory({
                category: { ...formData, accountId: accountData._id },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(setGoingToClose());
                }
            });
        } else {
            dispatch(updateProductCategory({
                category: { ...formData, _id: selectedCategory._id, accountId: selectedCategory.accountId },
                token
            })).then((result) => {
                if (!result.error) {
                    dispatch(setHasToRefresh());
                    dispatch(setGoingToClose());
                }
            });
        }
    };

    useEffect(() => {
        return () => {
            dispatch(resetCreateStatus());
            dispatch(resetUpdateStatus());
        };
    }, [dispatch]);

    if (mode === 'edit' && selectedCategoryLoading) {
        return <LoadingSection />;
    }

    // Opzioni gerarchiche per il select
    const buildHierarchicalOptions = () => {
        const options = [];
        const addOptions = (cats, level = 0) => {
            cats.forEach(cat => {
                if (cat._id === selectedCategory?._id) return;
                const prefix = '\u00A0\u00A0\u00A0\u00A0'.repeat(level);
                const label = level > 0 ? `${prefix}└ ${cat.name}` : cat.name;
                options.push({ value: cat._id, label });
                const children = categoriesList.filter(c => c.parentId === cat._id);
                if (children.length > 0) addOptions(children, level + 1);
            });
        };
        const roots = (categoriesList || []).filter(c => !c.parentId || c.parentId === '');
        addOptions(roots);
        return options;
    };

    const hierarchicalOptions = buildHierarchicalOptions();
    const parentName = modalData?.parentId
        ? categoriesList.find(c => c._id === modalData.parentId)?.name
        : null;

    const inputClass = "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
    const inputErrorClass = "w-full px-3 py-2 text-sm border border-red-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-red-50";

    return (
        <div className="p-5">
            {(createLoading || updateLoading) && <LoadingSection isFixed />}

            {/* Header compatto */}
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <DynamicIcon iconClass="fa-solid fa-folder text-yellow-600" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">
                        {mode === 'add' ? 'Nuova Categoria' : 'Modifica Categoria'}
                    </h2>
                    {mode === 'add' && parentName && (
                        <p className="text-xs text-gray-500 mt-0.5">
                            Sottocategoria di <span className="font-medium text-gray-700">{parentName}</span>
                        </p>
                    )}
                    {mode === 'edit' && (
                        <p className="text-xs text-gray-500 mt-0.5">Modifica i dati della categoria</p>
                    )}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Nome */}
                <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                        Nome *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={!validation.name ? inputErrorClass : inputClass}
                        placeholder="es. Materiale Elettrico"
                    />
                    {!validation.name && (
                        <p className="text-red-500 text-xs mt-1">Campo obbligatorio</p>
                    )}
                </div>

                {/* Categoria Genitore */}
                <div>
                    <label htmlFor="parentId" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                        Categoria Genitore
                    </label>
                    <select
                        id="parentId"
                        name="parentId"
                        value={formData.parentId}
                        onChange={handleInputChange}
                        className={inputClass}
                    >
                        <option value="">Nessuna (categoria principale)</option>
                        {hierarchicalOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Descrizione */}
                <div>
                    <label htmlFor="description" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                        Descrizione
                    </label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className={inputClass}
                        placeholder="Descrizione opzionale"
                        rows="2"
                    />
                </div>

                {/* Toggle Stato */}
                <div className="flex items-center justify-between py-3 px-3 bg-gray-50 rounded-lg">
                    <div>
                        <p className="text-sm font-medium text-gray-700">Categoria attiva</p>
                        <p className="text-xs text-gray-500">Le categorie inattive non saranno visibili</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.active}
                            onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                </div>

                {/* Errore */}
                {(createError || updateError) && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                        <DynamicIcon iconClass="fas fa-exclamation-circle flex-shrink-0" />
                        <span>{createError || updateError}</span>
                    </div>
                )}

                {/* Azioni */}
                <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <button
                        type="button"
                        className="secondary"
                        onClick={() => dispatch(closeModal())}
                        disabled={createLoading || updateLoading}
                    >
                        Annulla
                    </button>
                    <button
                        type="submit"
                        className="primary"
                        disabled={createLoading || updateLoading}
                    >
                        {createLoading || updateLoading ? (
                            <>
                                <DynamicIcon iconClass="fas fa-spinner fa-spin" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <DynamicIcon iconClass="fas fa-save" />
                                {mode === 'add' ? 'Crea' : 'Salva'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
